// Dok 55 – Samlad byggherrerapport: delegeringskedja och checklistrubriker per skede.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  BYGGHERRE_CHECKLIST_PHASES,
  BYGGHERRE_CHECKLIST_STATUS_LABELS,
  DELEGATION_LETTER_STATUS_LABELS,
} from "@/lib/basProcess/byggherreDocs";
import {
  generateByggherreReportPdf,
  generateByggherreReportWordDocument,
  loadByggherreReportData,
  type ByggherreReportData,
} from "@/lib/documentGenerator/byggherreReportGenerator";
import useByggherreAccess from "@/hooks/useByggherreAccess";

export const ByggherreReport: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [data, setData] = useState<ByggherreReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { canExport } = useByggherreAccess(projectId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await loadByggherreReportData(projectId));
    } catch {
      toast.error("Kunde inte läsa underlaget till rapporten");
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportWord = async () => {
    if (!data) return;
    setBusy(true);
    try {
      await generateByggherreReportWordDocument(data);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
    setBusy(false);
  };

  const exportPdf = async () => {
    if (!data) return;
    setBusy(true);
    try {
      await generateByggherreReportPdf(data);
    } catch {
      toast.error("Kunde inte skapa PDF:en");
    }
    setBusy(false);
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar rapporten...</div>;
  if (!data) return null;

  const openRequired = data.items.filter(
    (i) =>
      i.required &&
      !(i.status === "ok" || (i.status === "ej_aktuell" && (i.note || "").trim().length >= 5))
  );

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="55"
        title="Samlad byggherrerapport"
        description="Delegeringskedjan från Dok 51 och 52 tillsammans med checklistans rubriker per skede från Dok 53 och uppgiftsfördelningen i Dok 54 – i ett dokument."
        legalRef="AFS 2023:3 kap. 2 · AML 3 kap. 6 §"
        icon={FileText}
        exportHistory={{ documentType: "byggherre_rapport", projectId }}
        audit={{ documentType: "byggherre_checklista", projectId, title: "Ändringslogg – checklista byggherre" }}
        meta={
          <Badge variant={openRequired.length ? "destructive" : "outline"}>
            {openRequired.length
              ? `${openRequired.length} obligatoriska punkter öppna`
              : "Alla obligatoriska punkter kvitterade"}
          </Badge>
        }
        actions={
          canExport ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={exportWord} disabled={busy}>
                <Download className="mr-1 h-4 w-4" /> Word
              </Button>
              <Button size="sm" variant="outline" onClick={exportPdf} disabled={busy}>
                <Download className="mr-1 h-4 w-4" /> PDF
              </Button>
            </div>
          ) : null
        }
      />

      <Card>
        <CardContent className="space-y-3 pt-6">
          <h2 className="text-sm font-semibold">Delegeringskedja</h2>
          {data.letters.length ? (
            <ul className="divide-y">
              {data.letters.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span>
                    <span className="font-medium">{l.from_name || "–"}</span>
                    {" → "}
                    <span className="font-medium">{l.to_name || "–"}</span>
                    {l.deputy_name ? ` (ställföreträdare ${l.deputy_name})` : ""}
                  </span>
                  <Badge variant={l.status === "undertecknat" ? "secondary" : "outline"}>
                    {DELEGATION_LETTER_STATUS_LABELS[l.status] || l.status}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              Inga delegeringsbrev är registrerade ännu (Dok 52).
            </p>
          )}
        </CardContent>
      </Card>

      {BYGGHERRE_CHECKLIST_PHASES.map((phase) => {
        const rows = data.items.filter((i) => i.phase === phase.key);
        if (!rows.length) return null;
        return (
          <Card key={phase.key}>
            <CardContent className="space-y-2 pt-6">
              <h2 className="text-sm font-semibold">{phase.label}</h2>
              <ul className="divide-y">
                {rows.map((r, i) => {
                  const blocked =
                    r.required &&
                    !(r.status === "ok" || (r.status === "ej_aktuell" && (r.note || "").trim().length >= 5));
                  return (
                    <li key={`${phase.key}-${i}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        {blocked ? (
                          <AlertTriangle className="h-4 w-4 text-destructive" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        )}
                        {r.activity}
                        {r.required && <Badge variant="outline">Obligatorisk</Badge>}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {BYGGHERRE_CHECKLIST_STATUS_LABELS[r.status] || r.status}
                        {r.responsible ? ` · ${r.responsible}` : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ByggherreReport;
